import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  HardDriveIcon,
  CloudIcon,
  PlusIcon,
  Trash2Icon,
  SearchIcon,
  XIcon,
  AlertTriangleIcon,
  Maximize2Icon,
  ServerIcon,
  UnlinkIcon,
  ArrowUpDownIcon,
  Loader2Icon,
  MinusIcon,
} from 'lucide-react'
import { useState, useRef, useEffect } from 'react'
import { toast } from 'sonner'

import { ResponsivePageHeader } from '@nebula/shared/components'
import { ConfirmModal } from '@nebula/shared/components/modals'
import { usePermissions } from '@nebula/shared/hooks'
import { apiClient } from '@nebula/shared/services/api'
import blockStorageApi, {
  type BlockStorageVolume,
  type BlockStorageProduct,
  getVolumeStatusColor,
  getVolumeStatusLabel,
  formatStorageSize,
  formatMonthlyPrice,
  isVolumeTransitioning,
} from '@nebula/shared/services/blockStorageApi'
import { useAuthStore } from '@nebula/shared/store'

// ========================================
// Types
// ========================================

type CustomerVM = {
  instance_id: string
  instance_name: string
  status: string
  product: { id: string; name: string }
}

const VOLUME_SIZE_STEP_GB = 10

export const getVolumeSizeBounds = (product?: BlockStorageProduct) => {
  const minimum =
    Math.ceil((product?.min_storage ?? VOLUME_SIZE_STEP_GB) / VOLUME_SIZE_STEP_GB) *
    VOLUME_SIZE_STEP_GB
  const productMaximum =
    Math.floor((product?.max_storage ?? 10_000) / VOLUME_SIZE_STEP_GB) * VOLUME_SIZE_STEP_GB

  return { minimum, maximum: Math.max(minimum, productMaximum) }
}

export const normalizeVolumeSize = (rawGb: number, minimum: number, maximum: number) => {
  const rounded = Math.round(rawGb / VOLUME_SIZE_STEP_GB) * VOLUME_SIZE_STEP_GB
  return Math.min(maximum, Math.max(minimum, rounded))
}

// ========================================
// Component
// ========================================

const StoragePage = () => {
  const { token } = useAuthStore()
  const queryClient = useQueryClient()
  const { has } = usePermissions()
  const canCreateVolume = has('vms:create')
  const canManageVolume = has('vms:update')
  const canDeleteVolume = has('vms:delete')

  // --- Block Storage state ---
  const [volumeSearch, setVolumeSearch] = useState('')
  const [volumeStatusFilter, setVolumeStatusFilter] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showResizeModal, setShowResizeModal] = useState(false)
  const [showAttachModal, setShowAttachModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showDetachModal, setShowDetachModal] = useState(false)
  const [selectedVolume, setSelectedVolume] = useState<BlockStorageVolume | null>(null)

  // Create form
  const [createForm, setCreateForm] = useState({
    name: '',
    size_gb: 10,
    product_id: '',
    payment_option: 'credit_account' as 'credit_card' | 'credit_account',
  })
  // Free-text GB input for the volume size (kept in sync with the slider)
  const [sizeInput, setSizeInput] = useState('10')

  // Snaps a raw GB value to the nearest 10 GB multiple and respects the product limits.
  const setVolumeSize = (rawGb: number) => {
    const snapped = normalizeVolumeSize(rawGb, minimumVolumeSize, maximumVolumeSize)
    setCreateForm((current) => ({ ...current, size_gb: snapped }))
    setSizeInput(String(snapped))
  }

  // Resize form
  const [resizeNewSize, setResizeNewSize] = useState(0)
  const [resizeConfirm, setResizeConfirm] = useState(false)

  // Attach form
  const [attachVmId, setAttachVmId] = useState('')

  // Status transition detection
  const prevStatusMap = useRef<Record<string, string>>({})

  // ========================================
  // React Query — Block Storage
  // ========================================

  const { data: volumesData, isLoading: volumesLoading } = useQuery({
    queryKey: ['block-storage-volumes', volumeSearch, volumeStatusFilter],
    enabled: !!token,
    queryFn: () =>
      blockStorageApi.listVolumes({
        search: volumeSearch || undefined,
        status: volumeStatusFilter || undefined,
        limit: 50,
      }),
    refetchInterval: (query) => {
      const vols = query.state.data?.volumes || []
      const hasTransitioning = vols.some((v) => isVolumeTransitioning(v.status))
      return hasTransitioning ? 5000 : false
    },
  })

  const volumes = volumesData?.volumes || []

  const { data: products } = useQuery({
    queryKey: ['block-storage-products'],
    enabled: !!token && showCreateModal,
    queryFn: () => blockStorageApi.listProducts(),
  })

  const selectedProduct = products?.find((p: BlockStorageProduct) => p.id === createForm.product_id)
  const { minimum: minimumVolumeSize, maximum: maximumVolumeSize } =
    getVolumeSizeBounds(selectedProduct)

  const { data: vmsData } = useQuery({
    queryKey: ['end-customer-instances-for-attach'],
    enabled: !!token && showAttachModal,
    queryFn: async () => {
      const response = (await apiClient.get('/api/v1/end-customer/instances?limit=100')) as any
      return response as { instances: CustomerVM[] }
    },
  })

  const availableVMs: CustomerVM[] = (vmsData?.instances || []).filter(
    (vm: CustomerVM) => vm.status === 'running' || vm.status === 'stopped',
  )

  // Detect status transitions
  useEffect(() => {
    if (!volumes.length) return
    const prev = prevStatusMap.current
    for (const vol of volumes) {
      const oldStatus = prev[vol.id]
      if (oldStatus && oldStatus !== vol.status) {
        if (oldStatus === 'creating' && vol.status === 'available') {
          toast.success(`Volume "${vol.name}" criado com sucesso!`)
        } else if (oldStatus === 'attaching' && vol.status === 'attached') {
          toast.success(`Volume "${vol.name}" anexado com sucesso!`)
        } else if (oldStatus === 'detaching' && vol.status === 'available') {
          toast.success(`Volume "${vol.name}" desanexado com sucesso!`)
        } else if (oldStatus === 'detaching' && vol.status === 'attached') {
          // O detach não pôde concluir e o volume voltou ao estado anterior — o
          // caso típico é o PVE não conseguir desplugar o disco a quente, que só
          // um stop/start da VM resolve. Sem este ramo o pedido morria em
          // silêncio: o card volta a "Anexado" e nada explica o porquê.
          toast.error(
            `Não foi possível desanexar "${vol.name}": ${vol.status_message || 'motivo desconhecido'}`,
            { duration: 8000 },
          )
        } else if (
          oldStatus === 'resizing' &&
          (vol.status === 'available' || vol.status === 'attached')
        ) {
          toast.success(`Volume "${vol.name}" redimensionado com sucesso!`)
        } else if (oldStatus === 'deleting' && vol.status === 'deleted') {
          toast.success(`Volume "${vol.name}" excluído com sucesso!`)
        } else if (vol.status === 'error') {
          toast.error(`Erro no volume "${vol.name}": ${vol.status_message || 'erro desconhecido'}`)
        }
      }
    }
    const newMap: Record<string, string> = {}
    for (const vol of volumes) {
      newMap[vol.id] = vol.status
    }
    prevStatusMap.current = newMap
  }, [volumes])

  // ========================================
  // Mutations
  // ========================================

  const createVolumeMutation = useMutation({
    mutationFn: () =>
      blockStorageApi.createVolume({
        product_id: createForm.product_id,
        name: createForm.name,
        size_gb: createForm.size_gb,
        payment_option: createForm.payment_option,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['block-storage-volumes'] })
      toast.success('Volume sendo criado...')
      setShowCreateModal(false)
      setCreateForm({ name: '', size_gb: 10, product_id: '', payment_option: 'credit_account' })
      setSizeInput('10')
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || 'Erro ao criar volume')
    },
  })

  const deleteVolumeMutation = useMutation({
    mutationFn: (volumeId: string) => blockStorageApi.deleteVolume(volumeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['block-storage-volumes'] })
      toast.success('Volume sendo excluído...')
      setShowDeleteModal(false)
      setSelectedVolume(null)
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || 'Erro ao excluir volume')
    },
  })

  const attachVolumeMutation = useMutation({
    mutationFn: ({ volumeId, vmId }: { volumeId: string; vmId: string }) =>
      blockStorageApi.attachVolume(volumeId, { vm_instance_id: vmId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['block-storage-volumes'] })
      toast.success('Anexando volume à VM...')
      setShowAttachModal(false)
      setSelectedVolume(null)
      setAttachVmId('')
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || 'Erro ao anexar volume')
    },
  })

  const detachVolumeMutation = useMutation({
    mutationFn: (volumeId: string) => blockStorageApi.detachVolume(volumeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['block-storage-volumes'] })
      toast.success('Desanexando volume da VM...')
      setShowDetachModal(false)
      setSelectedVolume(null)
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || 'Erro ao desanexar volume')
    },
  })

  const resizeVolumeMutation = useMutation({
    mutationFn: ({ volumeId, newSize }: { volumeId: string; newSize: number }) =>
      blockStorageApi.resizeVolume(volumeId, { new_size_gb: newSize }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['block-storage-volumes'] })
      toast.success('Redimensionando volume...')
      setShowResizeModal(false)
      setSelectedVolume(null)
      setResizeNewSize(0)
      setResizeConfirm(false)
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || 'Erro ao redimensionar volume')
    },
  })

  // ========================================
  // Helpers
  // ========================================

  const openCreateModal = () => {
    setCreateForm({ name: '', size_gb: 10, product_id: '', payment_option: 'credit_account' })
    setSizeInput('10')
    setShowCreateModal(true)
  }

  const estimatedCost = selectedProduct
    ? createForm.size_gb * selectedProduct.price_per_storage_gb + selectedProduct.base_price
    : 0

  const openAttach = (vol: BlockStorageVolume) => {
    setSelectedVolume(vol)
    setAttachVmId('')
    setShowAttachModal(true)
  }

  const openDetach = (vol: BlockStorageVolume) => {
    setSelectedVolume(vol)
    setShowDetachModal(true)
  }

  const openResize = (vol: BlockStorageVolume) => {
    setSelectedVolume(vol)
    setResizeNewSize(vol.size_gb + 10)
    setResizeConfirm(false)
    setShowResizeModal(true)
  }

  const openDelete = (vol: BlockStorageVolume) => {
    setSelectedVolume(vol)
    setShowDeleteModal(true)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR')
  }

  // ========================================
  // Block Storage Tab (REAL API)
  // ========================================

  const statusFilters = [
    { value: '', label: 'Todos' },
    { value: 'available', label: 'Disponível' },
    { value: 'attached', label: 'Anexado' },
    { value: 'creating', label: 'Criando' },
    { value: 'error', label: 'Erro' },
  ]

  const renderVolumesTab = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Block Storage Volumes</h2>
          <p className="text-sm text-gray-600">Adicione storage adicional às suas VMs</p>
        </div>
        {canCreateVolume && (
          <button
            onClick={openCreateModal}
            className="flex items-center space-x-2 rounded-lg bg-purple-600 px-4 py-2 text-white hover:bg-purple-700"
          >
            <PlusIcon className="size-4" />
            <span>Criar Volume</span>
          </button>
        )}
      </div>

      {/* Search + Filters */}
      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <SearchIcon className="absolute top-1/2 left-3 size-4 -translate-y-1/2 transform text-gray-400" />
          <input
            type="text"
            value={volumeSearch}
            onChange={(e) => setVolumeSearch(e.target.value)}
            placeholder="Buscar volumes..."
            className="w-full rounded-lg border border-gray-300 py-2 pr-4 pl-10 focus:border-transparent focus:ring-2 focus:ring-purple-500"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {statusFilters.map((f) => (
            <button
              key={f.value}
              onClick={() => setVolumeStatusFilter(f.value)}
              className={`rounded-lg border px-3 py-2 text-sm transition-colors ${
                volumeStatusFilter === f.value
                  ? 'border-purple-300 bg-purple-100 text-purple-700'
                  : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Loading */}
      {volumesLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2Icon className="size-8 animate-spin text-purple-600" />
        </div>
      )}

      {/* Empty state */}
      {!volumesLoading && volumes.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-gray-500">
          <HardDriveIcon className="mb-4 size-16 text-gray-300" />
          <p className="text-lg font-medium">Nenhum volume encontrado</p>
          <p className="mt-1 text-sm">Crie um volume para adicionar storage às suas VMs</p>
          {canCreateVolume && (
            <button
              onClick={openCreateModal}
              className="mt-4 flex items-center space-x-2 rounded-lg bg-purple-600 px-4 py-2 text-white hover:bg-purple-700"
            >
              <PlusIcon className="size-4" />
              <span>Criar Volume</span>
            </button>
          )}
        </div>
      )}

      {/* Volumes Grid */}
      {!volumesLoading && volumes.length > 0 && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
          {volumes.map((volume) => (
            <div
              key={volume.id}
              className="rounded-lg bg-white shadow transition-shadow hover:shadow-md"
            >
              <div className="p-6">
                <div className="mb-4 flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate text-lg font-medium text-gray-900">{volume.name}</h3>
                    <div className="mt-1 flex items-center space-x-2 text-sm text-gray-500">
                      <span>{formatStorageSize(volume.size_gb)}</span>
                      <span>·</span>
                      <span className="uppercase">{volume.storage_type}</span>
                    </div>
                  </div>
                  <span
                    className={`ml-2 inline-flex items-center rounded-full px-2 py-1 text-xs font-medium whitespace-nowrap ${getVolumeStatusColor(volume.status)}`}
                  >
                    {isVolumeTransitioning(volume.status) && (
                      <Loader2Icon className="mr-1 size-3 animate-spin" />
                    )}
                    {getVolumeStatusLabel(volume.status)}
                  </span>
                </div>

                {/* Volume Info */}
                <div className="mb-4 space-y-2">
                  {/* Volume anexado a VM em exclusão vem sem `attached_vm` (o backend não
                      identifica a VM que sumiu do portal, issue #795). O volume é do
                      cliente e continua aqui, então a linha diz o estado em vez de sumir. */}
                  {(volume.attached_vm || volume.status === 'attached') && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Anexado a:</span>
                      <span className="font-medium text-gray-900">
                        {volume.attached_vm?.name || 'instância em exclusão'}
                      </span>
                    </div>
                  )}
                  {volume.attached_scsi_slot && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Slot:</span>
                      <span className="font-mono text-gray-900">{volume.attached_scsi_slot}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Custo mensal:</span>
                    <span className="font-medium text-gray-900">
                      {formatMonthlyPrice(volume.monthly_cost, volume.currency)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Criado:</span>
                    <span className="text-gray-900">{formatDate(volume.created_at)}</span>
                  </div>
                  {/* A mensagem também aparece FORA de `error`: um detach que o
                      PVE não pôde concluir devolve o volume a `attached` (o disco
                      continua na VM, íntegro e cobrado), e antes disso o motivo
                      ficava invisível — o card voltava a "Anexado" sem explicar
                      nada. Âmbar em vez de vermelho porque o volume está são; o
                      que falhou foi o pedido. */}
                  {volume.status_message && (
                    <div
                      className={`mt-2 rounded border p-2 text-xs ${
                        volume.status === 'error'
                          ? 'border-red-200 bg-red-50 text-red-700'
                          : 'border-amber-200 bg-amber-50 text-amber-800'
                      }`}
                    >
                      {volume.status_message}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between border-t pt-4">
                  <div className="flex items-center space-x-1">
                    {canManageVolume && volume.status === 'available' && (
                      <button
                        onClick={() => openAttach(volume)}
                        className="rounded-lg p-2 text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-700"
                        title="Anexar a uma VM"
                      >
                        <ServerIcon className="size-4" />
                      </button>
                    )}
                    {canManageVolume && volume.status === 'attached' && (
                      <button
                        onClick={() => openDetach(volume)}
                        className="rounded-lg p-2 text-orange-600 transition-colors hover:bg-orange-50 hover:text-orange-700"
                        title="Desanexar da VM"
                      >
                        <UnlinkIcon className="size-4" />
                      </button>
                    )}
                    {canManageVolume &&
                      (volume.status === 'available' || volume.status === 'attached') && (
                        <button
                          onClick={() => openResize(volume)}
                          className="rounded-lg p-2 text-purple-600 transition-colors hover:bg-purple-50 hover:text-purple-700"
                          title="Redimensionar volume"
                        >
                          <ArrowUpDownIcon className="size-4" />
                        </button>
                      )}
                  </div>
                  {canDeleteVolume && volume.status === 'available' && (
                    <button
                      onClick={() => openDelete(volume)}
                      className="rounded-lg p-2 text-gray-400 transition-colors hover:text-red-600"
                      title="Excluir volume"
                    >
                      <Trash2Icon className="size-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )

  // ========================================
  // Create Volume Modal
  // ========================================

  const renderCreateModal = () => {
    if (!showCreateModal) return null

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
        <div className="mx-4 w-full max-w-lg overflow-hidden rounded-2xl border border-white/70 bg-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-gray-100 bg-linear-to-r from-purple-50 to-white px-6 py-5">
            <div className="flex items-center gap-3">
              <div className="flex size-10 items-center justify-center rounded-xl bg-purple-600 text-white shadow-sm shadow-purple-200">
                <HardDriveIcon className="size-5" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-950">Criar novo volume</h3>
                <p className="mt-0.5 text-xs text-gray-500">
                  Storage adicional pronto para anexar às suas VMs
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowCreateModal(false)}
              className="flex size-9 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
              aria-label="Fechar"
            >
              <XIcon className="size-5" />
            </button>
          </div>

          <div className="space-y-5 p-6">
            {/* Product selection */}
            <div>
              <label className="mb-2 block text-sm font-semibold text-gray-800">Produto</label>
              {products && products.length === 0 && (
                <div className="mb-2 rounded-lg border border-yellow-200 bg-yellow-50 p-3">
                  <div className="flex items-center space-x-2 text-yellow-700">
                    <AlertTriangleIcon className="size-4 shrink-0" />
                    <span className="text-sm">
                      Nenhum produto de storage disponível no seu provedor. Entre em contato com seu
                      provedor.
                    </span>
                  </div>
                </div>
              )}
              <select
                value={createForm.product_id}
                onChange={(e) => {
                  const productId = e.target.value
                  const product = products?.find((p: BlockStorageProduct) => p.id === productId)
                  const { minimum, maximum } = getVolumeSizeBounds(product)
                  const normalizedSize = normalizeVolumeSize(createForm.size_gb, minimum, maximum)
                  setCreateForm({ ...createForm, product_id: productId, size_gb: normalizedSize })
                  setSizeInput(String(normalizedSize))
                }}
                className="w-full rounded-xl border border-gray-200 bg-gray-50/70 px-3.5 py-2.5 transition-colors focus:border-transparent focus:bg-white focus:ring-2 focus:ring-purple-500"
              >
                <option value="">Selecione um produto</option>
                {(products || []).map((p: BlockStorageProduct) => (
                  <option key={p.id} value={p.id}>
                    {p.name} —{' '}
                    {new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: p.currency,
                    }).format(p.price_per_storage_gb)}
                    /GB/mês
                  </option>
                ))}
              </select>
            </div>

            {/* Name */}
            <div>
              <label className="mb-2 block text-sm font-semibold text-gray-800">
                Nome do volume
              </label>
              <input
                type="text"
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                className="w-full rounded-xl border border-gray-200 bg-gray-50/70 px-3.5 py-2.5 transition-colors focus:border-transparent focus:bg-white focus:ring-2 focus:ring-purple-500"
                placeholder="ex: database-storage"
                maxLength={255}
              />
            </div>

            {selectedProduct ? (
              <>
                {/* Size */}
                <div className="rounded-2xl border border-purple-100 bg-linear-to-br from-purple-50/80 to-white p-4">
                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-gray-900">
                      Tamanho do volume
                    </label>
                    <p className="mt-0.5 text-xs text-gray-500">
                      Defina a capacidade que deseja contratar
                    </p>
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <button
                      type="button"
                      onClick={() => setVolumeSize(createForm.size_gb - VOLUME_SIZE_STEP_GB)}
                      disabled={createForm.size_gb <= minimumVolumeSize}
                      className="flex size-11 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-600 shadow-sm transition-colors hover:border-purple-300 hover:text-purple-700 disabled:cursor-not-allowed disabled:opacity-40"
                      aria-label="Diminuir 10 GB"
                    >
                      <MinusIcon className="size-4" />
                    </button>
                    <div className="relative">
                      <input
                        type="number"
                        value={sizeInput}
                        onChange={(e) => setSizeInput(e.target.value)}
                        onBlur={() => {
                          const raw = Number.parseInt(sizeInput, 10)
                          setVolumeSize(Number.isNaN(raw) ? minimumVolumeSize : raw)
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') e.currentTarget.blur()
                        }}
                        min={minimumVolumeSize}
                        max={maximumVolumeSize}
                        step={VOLUME_SIZE_STEP_GB}
                        aria-label="Tamanho do volume em GB"
                        className="h-11 w-36 [appearance:textfield] rounded-xl border border-gray-200 bg-white pr-12 pl-4 text-center text-lg font-semibold text-gray-950 shadow-sm [-moz-appearance:textfield] focus:border-transparent focus:ring-2 focus:ring-purple-500 [&::-webkit-inner-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0 [&::-webkit-outer-spin-button]:appearance-none"
                      />
                      <span className="pointer-events-none absolute top-1/2 right-4 -translate-y-1/2 text-xs font-semibold text-gray-400">
                        GB
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setVolumeSize(createForm.size_gb + VOLUME_SIZE_STEP_GB)}
                      disabled={createForm.size_gb >= maximumVolumeSize}
                      className="flex size-11 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-600 shadow-sm transition-colors hover:border-purple-300 hover:text-purple-700 disabled:cursor-not-allowed disabled:opacity-40"
                      aria-label="Aumentar 10 GB"
                    >
                      <PlusIcon className="size-4" />
                    </button>
                  </div>
                  <input
                    type="range"
                    value={createForm.size_gb}
                    onChange={(e) => setVolumeSize(Number.parseInt(e.target.value, 10))}
                    min={minimumVolumeSize}
                    max={maximumVolumeSize}
                    step={VOLUME_SIZE_STEP_GB}
                    className="mt-5 w-full accent-purple-600"
                    aria-label="Selecionar tamanho do volume"
                  />
                  <div className="mt-1 flex justify-between text-xs text-gray-500">
                    <span>{formatStorageSize(minimumVolumeSize)}</span>
                    <span>{formatStorageSize(maximumVolumeSize)}</span>
                  </div>
                  <p className="mt-2 text-center text-xs text-gray-500">
                    Digite o tamanho desejado. Ao sair do campo, o valor será arredondado para o
                    múltiplo de 10 GB mais próximo.
                  </p>
                </div>

                {/* Price estimate */}
                <div className="rounded-2xl border border-purple-100 bg-purple-50 p-4 shadow-sm shadow-purple-100/60">
                  <div className="flex justify-between text-sm">
                    <span className="text-purple-700">Custo mensal estimado</span>
                    <span className="text-base font-bold text-purple-950">
                      {formatMonthlyPrice(estimatedCost, selectedProduct.currency)}
                    </span>
                  </div>
                </div>
              </>
            ) : (
              <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">
                Selecione um produto para definir a capacidade e consultar o valor do volume.
              </div>
            )}
          </div>

          <div className="flex items-center justify-end space-x-3 border-t border-gray-100 bg-gray-50/70 px-6 py-4">
            <button
              onClick={() => setShowCreateModal(false)}
              className="rounded-xl px-4 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-200"
            >
              Cancelar
            </button>
            <button
              onClick={() => createVolumeMutation.mutate()}
              disabled={
                createVolumeMutation.isPending || !createForm.name || !createForm.product_id
              }
              className="flex items-center space-x-2 rounded-xl bg-purple-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm shadow-purple-200 transition-colors hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {createVolumeMutation.isPending && <Loader2Icon className="size-4 animate-spin" />}
              <span>{createVolumeMutation.isPending ? 'Criando...' : 'Criar Volume'}</span>
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ========================================
  // Resize Volume Modal
  // ========================================

  const renderResizeModal = () => {
    if (!showResizeModal || !selectedVolume) return null

    return (
      <div className="bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black p-4">
        <div className="mx-4 w-full max-w-md rounded-lg bg-white shadow-xl">
          <div className="flex items-center justify-between border-b p-6">
            <h3 className="text-lg font-semibold text-gray-900">Redimensionar Volume</h3>
            <button
              onClick={() => setShowResizeModal(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <XIcon className="size-5" />
            </button>
          </div>

          <div className="space-y-4 p-6">
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <div className="flex items-center space-x-2 text-blue-800">
                <Maximize2Icon className="size-5" />
                <span className="font-medium">{selectedVolume.name}</span>
              </div>
              <p className="mt-1 text-sm text-blue-700">
                Tamanho atual: {formatStorageSize(selectedVolume.size_gb)}
              </p>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Novo Tamanho: {formatStorageSize(resizeNewSize)}
              </label>
              <input
                type="range"
                value={resizeNewSize}
                onChange={(e) => setResizeNewSize(Number.parseInt(e.target.value))}
                min={selectedVolume.size_gb + 1}
                max={10_000}
                step={10}
                className="w-full accent-purple-600"
              />
              <div className="mt-1 flex justify-between text-xs text-gray-500">
                <span>{formatStorageSize(selectedVolume.size_gb + 1)}</span>
                <span>{formatStorageSize(10_000)}</span>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                O novo tamanho deve ser maior que o atual (
                {formatStorageSize(selectedVolume.size_gb)})
              </p>
            </div>

            {/* New cost */}
            <div className="rounded-lg border border-purple-200 bg-purple-50 p-3">
              <div className="flex justify-between text-sm">
                <span className="text-purple-700">Novo custo mensal:</span>
                <span className="font-bold text-purple-900">
                  {formatMonthlyPrice(
                    resizeNewSize * selectedVolume.unit_price_per_gb,
                    selectedVolume.currency,
                  )}
                </span>
              </div>
            </div>

            <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
              <div className="flex items-start space-x-2">
                <AlertTriangleIcon className="mt-0.5 size-5 shrink-0 text-yellow-600" />
                <div className="text-sm text-yellow-800">
                  <p className="font-medium">Atenção:</p>
                  <ul className="mt-1 list-inside list-disc space-y-1">
                    <li>O volume não pode ser reduzido após o resize</li>
                    <li>O resize pode levar alguns minutos</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="confirm_resize"
                checked={resizeConfirm}
                onChange={(e) => setResizeConfirm(e.target.checked)}
                className="size-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <label htmlFor="confirm_resize" className="ml-2 block text-sm text-gray-900">
                Eu entendo que esta operação não pode ser desfeita
              </label>
            </div>
          </div>

          <div className="flex items-center justify-end space-x-3 border-t p-6">
            <button
              onClick={() => setShowResizeModal(false)}
              className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
            >
              Cancelar
            </button>
            <button
              onClick={() =>
                resizeVolumeMutation.mutate({ volumeId: selectedVolume.id, newSize: resizeNewSize })
              }
              disabled={
                resizeVolumeMutation.isPending ||
                !resizeConfirm ||
                resizeNewSize <= selectedVolume.size_gb
              }
              className="flex items-center space-x-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {resizeVolumeMutation.isPending && <Loader2Icon className="size-4 animate-spin" />}
              <span>
                {resizeVolumeMutation.isPending ? 'Redimensionando...' : 'Confirmar Resize'}
              </span>
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ========================================
  // Attach Volume Modal
  // ========================================

  const renderAttachModal = () => {
    if (!showAttachModal || !selectedVolume) return null

    return (
      <div className="bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black p-4">
        <div className="mx-4 w-full max-w-md rounded-lg bg-white shadow-xl">
          <div className="flex items-center justify-between border-b p-6">
            <h3 className="text-lg font-semibold text-gray-900">Anexar Volume</h3>
            <button
              onClick={() => setShowAttachModal(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <XIcon className="size-5" />
            </button>
          </div>

          <div className="space-y-4 p-6">
            <div className="rounded-lg border border-green-200 bg-green-50 p-4">
              <div className="flex items-center space-x-2 text-green-800">
                <HardDriveIcon className="size-5" />
                <span className="font-medium">{selectedVolume.name}</span>
              </div>
              <p className="mt-1 text-sm text-green-700">
                {formatStorageSize(selectedVolume.size_gb)} ·{' '}
                {selectedVolume.storage_type.toUpperCase()}
              </p>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Máquina Virtual
              </label>
              <select
                value={attachVmId}
                onChange={(e) => setAttachVmId(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-purple-500"
              >
                <option value="">Selecione uma VM</option>
                {availableVMs.map((vm) => (
                  <option key={vm.instance_id} value={vm.instance_id}>
                    {vm.instance_name} ({vm.status})
                  </option>
                ))}
              </select>
              {availableVMs.length === 0 && (
                <p className="mt-1 text-xs text-gray-500">
                  Nenhuma VM disponível. Crie uma VM primeiro.
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-end space-x-3 border-t p-6">
            <button
              onClick={() => setShowAttachModal(false)}
              className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
            >
              Cancelar
            </button>
            <button
              onClick={() =>
                attachVolumeMutation.mutate({ volumeId: selectedVolume.id, vmId: attachVmId })
              }
              disabled={attachVolumeMutation.isPending || !attachVmId}
              className="flex items-center space-x-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {attachVolumeMutation.isPending && <Loader2Icon className="size-4 animate-spin" />}
              <span>{attachVolumeMutation.isPending ? 'Anexando...' : 'Anexar Volume'}</span>
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ========================================
  // Main Render
  // ========================================

  return (
    <div className="mobile-page-padding">
      <ResponsivePageHeader
        title="Block Storage"
        description="Gerencie volumes de armazenamento para suas VMs"
        icon={HardDriveIcon}
        actions={
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <CloudIcon className="size-4" />
            <span>Centro de Storage</span>
          </div>
        }
      />

      {/* Content */}
      {renderVolumesTab()}

      {/* Block Storage Modals */}
      {renderCreateModal()}
      {renderResizeModal()}
      {renderAttachModal()}

      {/* Detach Confirm */}
      <ConfirmModal
        isOpen={showDetachModal}
        onClose={() => {
          setShowDetachModal(false)
          setSelectedVolume(null)
        }}
        onConfirm={() => selectedVolume && detachVolumeMutation.mutate(selectedVolume.id)}
        title="Desanexar Volume"
        message={
          selectedVolume?.attached_vm?.name
            ? `Tem certeza que deseja desanexar o volume "${selectedVolume?.name}" da VM "${selectedVolume.attached_vm.name}"?`
            : `Tem certeza que deseja desanexar o volume "${selectedVolume?.name}"?`
        }
        variant="warning"
        confirmText={detachVolumeMutation.isPending ? 'Desanexando...' : 'Desanexar'}
        isLoading={detachVolumeMutation.isPending}
      />

      {/* Delete Confirm */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false)
          setSelectedVolume(null)
        }}
        onConfirm={() => selectedVolume && deleteVolumeMutation.mutate(selectedVolume.id)}
        title="Excluir Volume"
        message={`Tem certeza que deseja excluir o volume "${selectedVolume?.name}" (${selectedVolume ? formatStorageSize(selectedVolume.size_gb) : ''})? Esta ação não pode ser desfeita.`}
        variant="danger"
        confirmText={deleteVolumeMutation.isPending ? 'Excluindo...' : 'Excluir Volume'}
        isLoading={deleteVolumeMutation.isPending}
      />
    </div>
  )
}

export default StoragePage
